import { nodeTitle, portLabel } from "./nodeDisplay";
import type { FlowDocument, FlowNode } from "./types";

/**
 * Advice about what a caller will EXPERIENCE, as distinct from whether the
 * document is valid.
 *
 * Deliberately a separate type from validateFlow.ts's `Diagnostic`. That one
 * mirrors Go's own diagnostics (`services/api/internal/flow/diagnostics.go`)
 * and its `Layer` union is part of a shared contract; everything in it
 * describes a document the server will REFUSE to publish. Nothing in this
 * file does. Every flow flagged here is schema-valid, semantically valid and
 * publishes without complaint — it just behaves badly on a real phone call.
 * Folding these into `Diagnostic` would quietly claim the Go validator
 * produces them, which it does not, and the plan's own note about not adding
 * client-only checks that reject what the server accepts is exactly this
 * trap. So: advice, never a publish gate.
 *
 * Why this exists at all: on 2026-11-03 a tester pressed 1 on the
 * `bill-reminder` template and the call went dead. Nothing was broken. The
 * flow is `menu --key:1--> mark (tag) --next--> end (hangup)`: the digit was
 * received, the tag was recorded, the call hung up, and because `tag` makes
 * no sound the caller heard silence and assumed a crash. The run log shows
 * the keypress working perfectly (`trace: start>amd>menu>mark>end`). A
 * validator had nothing to say because there is nothing invalid about it.
 */
export interface Advice {
  /** Stable identifier for the kind of problem. */
  code: string;
  /** The node the advice is about. */
  nodeId: string;
  /** One sentence a flow author can act on. */
  detail: string;
}

/** A caller pressed a key and the call ended without saying anything. */
export const CODE_SILENT_AFTER_INPUT = "silent_after_input";

/** Nothing on this path ever reaches a hangup or a transfer. */
export const CODE_NO_TERMINAL = "no_terminal_path";

/**
 * The node types that actually produce audio, taken from the executor's own
 * dispatch (`services/api/internal/flow/execute.go`'s `step`): `play`
 * resolves an asset and plays it, `tts` renders and speaks, and `gather`
 * plays its own prompt before collecting digits. Everything else is silent
 * from the caller's point of view — `tag`, `consent`, `http` and `branch` do
 * their work without a sound, which is the whole reason this check is
 * needed. `transfer` is not audio, but it hands the caller to someone else,
 * so a path ending there is not a silent drop either.
 */
const SPEAKS = new Set(["play", "tts", "gather"]);

function nodesById(document: FlowDocument): Map<string, FlowNode> {
  const map = new Map<string, FlowNode>();
  for (const node of document.nodes) {
    if (node && typeof node.id === "string") map.set(node.id, node);
  }
  return map;
}

function edgesBySource(document: FlowDocument): Map<string, FlowDocument["edges"]> {
  const map = new Map<string, FlowDocument["edges"]>();
  for (const edge of document.edges) {
    if (!edge || typeof edge.source !== "string") continue;
    const list = map.get(edge.source);
    if (list) list.push(edge);
    else map.set(edge.source, [edge]);
  }
  return map;
}

/**
 * Walking forward from `startId`, will the caller hear anything before the
 * call ends?
 *
 * True as soon as ANY reachable path speaks, not only when every path does.
 * That asymmetry is on purpose: this answers "could the caller plausibly
 * hear something", and a flow where one branch speaks is not the silent-drop
 * bug being looked for. Being generous here keeps the advice quiet unless
 * the outcome really is silence, because advice nobody can act on is worse
 * than no advice.
 *
 * `transfer` counts as a non-silent ending for the reason given on SPEAKS.
 * Cycles are handled by the visited set, so a repeat loop terminates here
 * rather than hanging the browser.
 */
function reachesAudio(
  startId: string,
  nodes: Map<string, FlowNode>,
  edges: Map<string, FlowDocument["edges"]>,
): boolean {
  const seen = new Set<string>();
  const queue = [startId];
  while (queue.length > 0) {
    const id = queue.shift();
    if (id === undefined || seen.has(id)) continue;
    seen.add(id);
    const node = nodes.get(id);
    if (!node) continue;
    if (SPEAKS.has(node.type) || node.type === "transfer") return true;
    for (const edge of edges.get(id) ?? []) {
      if (typeof edge.target === "string") queue.push(edge.target);
    }
  }
  return false;
}

/**
 * Every digit branch of every `gather` that leads to the call ending without
 * a word being spoken.
 *
 * Only the digit branches (`key:*` ports), not `timeout` or `invalid`. A
 * caller who pressed nothing and gets hung up on has at least not been
 * contradicted; one who pressed a key has been given no acknowledgement that
 * anything happened, which is the complaint this came from. `key:*` in the
 * sense of the literal `*` key is still a digit branch and is checked like
 * any other — it is only "repeat" by convention, not by contract.
 */
export function checkSilentAfterInput(document: FlowDocument): Advice[] {
  const nodes = nodesById(document);
  const edges = edgesBySource(document);
  const advice: Advice[] = [];

  for (const node of document.nodes) {
    if (!node || node.type !== "gather") continue;
    for (const edge of edges.get(node.id) ?? []) {
      if (!edge.port?.startsWith("key:")) continue;
      if (reachesAudio(edge.target, nodes, edges)) continue;
      const target = nodes.get(edge.target);
      const targetName = target ? nodeTitle(target.type) : edge.target;
      advice.push({
        code: CODE_SILENT_AFTER_INPUT,
        nodeId: node.id,
        detail:
          `"${portLabel(edge.port)}" on ${nodeTitle(node.type)} "${node.id}" leads to ` +
          `${targetName} and then ends the call without playing anything. The caller ` +
          `presses the key and hears silence. Add a "Say text" or "Play audio" node ` +
          `before the hang up so the call confirms what happened.`,
      });
    }
  }
  return advice;
}

/**
 * Paths that run out of nodes without hanging up or transferring.
 *
 * Distinct from a cycle (cycles.ts owns that) and from a missing required
 * port (requiredPorts.ts owns that): this is a port that IS wired, to a node
 * from which no hangup or transfer is reachable at all. The call stays up
 * doing nothing until something else tears it down, which reads to a caller
 * as a hung line.
 */
export function checkNoTerminal(document: FlowDocument): Advice[] {
  const nodes = nodesById(document);
  const edges = edgesBySource(document);
  const advice: Advice[] = [];

  const terminates = (startId: string): boolean => {
    const seen = new Set<string>();
    const queue = [startId];
    while (queue.length > 0) {
      const id = queue.shift();
      if (id === undefined || seen.has(id)) continue;
      seen.add(id);
      const node = nodes.get(id);
      if (!node) continue;
      if (node.type === "hangup" || node.type === "transfer") return true;
      for (const edge of edges.get(id) ?? []) {
        if (typeof edge.target === "string") queue.push(edge.target);
      }
    }
    return false;
  };

  if (document.nodes.length > 0 && !terminates(document.start_node_id)) {
    advice.push({
      code: CODE_NO_TERMINAL,
      nodeId: document.start_node_id,
      detail:
        "No path from the start of this flow reaches a Hang up or Transfer node. " +
        "The call will stay connected with nothing left to do.",
    });
  }
  return advice;
}

/** Every advisory check, in the order a reader should see them. */
export function adviseFlow(document: FlowDocument): Advice[] {
  return [...checkNoTerminal(document), ...checkSilentAfterInput(document)];
}
